import React from 'react';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { AssetType } from '@atlassian/jira-global-asset-types/src/types';
import {
	ASSETS_CF_TYPE,
	CASCADING_SELECT_CF_TYPE,
	DATETIME_CF_TYPE,
	DATE_CF_TYPE,
	DESCRIPTION_FIELD_TYPE,
	ENVIRONMENT_FIELD_TYPE,
	GROUP_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	MULTI_GROUP_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	MULTI_VERSION_CF_TYPE,
	NUMBER_CF_TYPE,
	PEOPLE_CF_TYPE,
	SELECT_CF_TYPE,
	SERVICE_ENTITY_CF_TYPE,
	TEXT_AREA_CF_TYPE,
	TEXT_CF_TYPE,
	VERSION_CF_TYPE,
} from '@atlassian/jira-project-settings-apps-common/src/consts';
import {
	type FieldConfigurationComponentProps,
	type FieldCategoryConfiguration,
	type EmptyStateComponentProps,
	type ActiveFieldLayoutItem,
	type ActiveLayoutItem,
	type AssociatedField,
	type Field,
	type FieldCategoryId,
	type FieldOperations,
	type FieldOptions,
	type FieldProvider,
	getLayoutItemFromAssociatedField,
	EmptyState,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features';
import { mockMessageDescriptor } from '../messages';

export const assetTypes: AssetType[] = [
	{
		origin: {
			appKey: 'com.saffron',
			originId: 'ASSET_1',
		},
		label: {
			value: 'Laptop',
		},
	},
	{
		origin: {
			appKey: 'com.saffron',
			originId: 'ASSET_4',
		},
		label: {
			value: 'Phone',
		},
	},
	{
		origin: {
			appKey: 'com.saffron',
			originId: 'ASSET_3',
		},
		label: {
			value: 'Tablet',
		},
	},
];

export const defaultFieldProvider: FieldProvider = {
	key: 'jira-platform',
	name: 'Jira',
};

export const defaultFieldOperations: FieldOperations = {
	editable: true,
	canModifyRequired: true,
	canModifyOptions: true,
	canModifyDefaultValue: true,
	canModifyPropertyConfiguration: true,
	removable: true,
	categoriesWhitelist: [],
	canAssociateInSettings: true,
	deletable: true,
};

export const fieldCategoryId: FieldCategoryId = 'my-field-category';

export const numberFieldWithNoDefaultValue: AssociatedField = {
	type: NUMBER_CF_TYPE,
	custom: false,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0010',
	description: 'valid field description',
	name: 'Coconut chocolate',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const affectedServicesField: AssociatedField = {
	type: SERVICE_ENTITY_CF_TYPE,
	custom: true,
	global: false,
	key: 'validAffectedServicesField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeefoo0d',
	description: 'valid affected services description',
	name: 'Le services',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const createField = (name: string): Field => ({
	key: `mock-field-${name}`,
	externalUuid: name,
	name,
	type: name,
	custom: false,
	global: true,
	description: '',
	operations: {
		editable: true,
		canModifyRequired: true,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: false,
	},
	provider: defaultFieldProvider,
	availability: null,
});

export const assetField: AssociatedField = {
	type: ASSETS_CF_TYPE,
	custom: true,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef000e',
	description: 'valid field description',
	name: 'Laptops',
	category: 'category-1',
	required: false,
	operations: {
		editable: true,
		canModifyRequired: true,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: true,
	},
	configuration: {
		assetTypes: [],
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const assignee: Field = {
	key: 'assignee',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0001',
	name: 'Assignee',
	type: 'assignee',
	custom: false,
	global: true,
	description: '',
	operations: {
		editable: false,
		canModifyRequired: false,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: false,
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const associatedAssigneeField: AssociatedField = {
	...assignee,
	custom: false,
	global: false,
	required: false,
	category: fieldCategoryId,
};

export const assigneeLayoutItem: ActiveLayoutItem =
	getLayoutItemFromAssociatedField(associatedAssigneeField);

export const textFieldWithNoDefaultValue: AssociatedField = {
	type: TEXT_CF_TYPE,
	custom: false,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0011',
	description: 'valid field description',
	name: 'Coconut chocolate description',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const descriptionField: AssociatedField = {
	type: DESCRIPTION_FIELD_TYPE,
	custom: false,
	global: false,
	key: 'description',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0016',
	description: '',
	name: 'Description',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const labels: Field = {
	key: 'labels',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0002',
	name: 'Labels',
	type: 'labels',
	custom: false,
	global: false,
	description: '',
	operations: {
		editable: false,
		canModifyRequired: true,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: false,
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const associatedLabelsField: AssociatedField = {
	...labels,
	custom: false,
	global: false,
	required: false,
	category: fieldCategoryId,
};

export const associatedLabelsFieldWithDefaultValue: AssociatedField = {
	...associatedLabelsField,
	defaultValue: 'Smurf, DarthVader',
};

export const labelsLayoutItem: ActiveFieldLayoutItem =
	getLayoutItemFromAssociatedField(associatedLabelsField);

export const validOptions: FieldOptions = [
	{
		value: 'Vanilla',
		id: 1,
		optimisticId: '1',
		externalUuid: '11111111-1a1a-2b2b-3c3c-12345678abcd',
	},
	{
		value: 'Chocolate',
		id: 2,
		optimisticId: '2',
		externalUuid: '22222222-1a1a-2b2b-3c3c-12345678abcd',
	},
	{
		value: 'Smurf',
		id: 3,
		optimisticId: '3',
		externalUuid: '33333333-c80a-4c6d-3c3c-12345678abcd',
	},
];

export const checkboxField: AssociatedField = {
	type: MULTI_SELECT_CF_TYPE,
	options: validOptions,
	custom: true,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0012',
	description: 'validField description',
	name: 'Ice cream flavor',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const validCascadingOptions: FieldOptions = [
	{
		value: 'Russia',
		id: 1,
		optimisticId: '1',
		externalUuid: '11111111-1a1a-2b2b-3c3c-12345678abcd',
	},
	{
		value: 'Australia',
		id: 2,
		optimisticId: '2',
		externalUuid: '22222222-1a1a-2b2b-3c3c-12345678abcd',
	},
	{
		value: 'Moscow',
		id: 3,
		parentId: 1,
		parentExternalUuid: '11111111-1a1a-2b2b-3c3c-12345678abcd',
		optimisticId: '3',
		externalUuid: '11112222-1a1a-2b2b-3c3c-12345678abcd',
	},
	{
		value: 'Saint-Petersburg',
		id: 4,
		parentId: 1,
		parentExternalUuid: '11111111-1a1a-2b2b-3c3c-12345678abcd',
		optimisticId: '4',
		externalUuid: '11113333-1a1a-2b2b-3c3c-12345678abcd',
	},
	{
		value: 'Sydney',
		id: 5,
		parentId: 2,
		parentExternalUuid: '22222222-1a1a-2b2b-3c3c-12345678abcd',
		optimisticId: '5',
		externalUuid: '22224444-1a1a-2b2b-3c3c-12345678abcd',
	},
	{
		value: 'Brisbane',
		id: 6,
		parentId: 2,
		parentExternalUuid: '22222222-1a1a-2b2b-3c3c-12345678abcd',
		optimisticId: '6',
		externalUuid: '22225555-1a1a-2b2b-3c3c-12345678abcd',
	},
];

export const cascadingDropdownWithNoDefaultValue: AssociatedField = {
	type: CASCADING_SELECT_CF_TYPE,
	options: validCascadingOptions,
	custom: true,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0012',
	description: 'validField description',
	name: 'Ice cream flavor',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const dateFieldWithNoDefaultValue: AssociatedField = {
	type: DATE_CF_TYPE,
	custom: false,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0015',
	description: 'valid field description',
	name: 'Coconut chocolate',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const dateFieldWithDefaultValue: AssociatedField = {
	...dateFieldWithNoDefaultValue,
	defaultValue: {
		useCurrentDate: true,
	},
};

export const dateFieldWithFixedDefaultValue: AssociatedField = {
	...dateFieldWithNoDefaultValue,
	defaultValue: {
		fixedDate: '2020-08-02',
	},
};

export const datetimeFieldWithNoDefaultValue: AssociatedField = {
	type: DATETIME_CF_TYPE,
	custom: false,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0014',
	description: 'valid field description',
	name: 'Coconut chocolate',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const datetimeFieldWithDefaultValue: AssociatedField = {
	...datetimeFieldWithNoDefaultValue,
	defaultValue: {
		useCurrentTime: true,
	},
};

export const datetimeFieldWithFixedDefaultValue: AssociatedField = {
	...datetimeFieldWithNoDefaultValue,
	defaultValue: {
		fixedTime: '2020-08-03T09:30+1000',
	},
};

export const exampleADF: ADFEntity = {
	version: 1,
	type: 'doc',
	content: [
		{
			type: 'paragraph',
			content: [
				{
					type: 'text',
					text: 'Place the coconut cream, white chocolate and salt in a heat- proof bowl over a saucepan half-filled with simmering water. Stir occasionally until the chocolate melts and the mixture is smooth. Remove from the heat and stir in the Malibu. Place in the fridge until firm enough to scoop (around 2 hours).',
				},
			],
		},
	],
};

const mockEmptyState =
	(message: string) =>
	(props: EmptyStateComponentProps): JSX.Element => (
		<EmptyState {...props} emptyStateText={mockMessageDescriptor(message)} />
	);

export const sampleCategoryConfiguration: FieldCategoryConfiguration = {
	name: 'example-category',
	displayName: mockMessageDescriptor('Field category'),
	emptyStateText: mockMessageDescriptor('There is nothing here'),
	categoryEmptyState: mockEmptyState('There is still nothing here but now in smaller font'),
	allowRequiredField: true,
	allowFieldAlias: false,
	allowFieldAliasEditing: false,
	allowedLayoutItems: [],
};

export const sampleCategoryConfiguration2: FieldCategoryConfiguration = {
	...sampleCategoryConfiguration,
	name: 'secondary',
	displayName: mockMessageDescriptor('Field category 2'),
};

export const fieldCategoriesConfigurationArray: FieldCategoryConfiguration[] = [
	sampleCategoryConfiguration,
	sampleCategoryConfiguration2,
];

export const peopleFieldWithNoDefaultValue: AssociatedField = {
	type: PEOPLE_CF_TYPE,
	custom: true,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef000e',
	description: 'valid field description',
	name: 'Magicians',
	category: fieldCategoryId,
	required: false,
	operations: {
		editable: true,
		canModifyRequired: true,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: true,
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const peopleFieldWithDefaultValue: AssociatedField = {
	...peopleFieldWithNoDefaultValue,
	defaultValue: [
		{ id: '#4321', name: 'Harry Potter', avatarUrl: '' },
		{ id: '#12345', name: 'Voldemort' },
	],
};

export const textAreaField: AssociatedField = {
	type: TEXT_AREA_CF_TYPE,
	custom: true,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0016',
	description: '',
	name: 'Paragraph',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const environmentField: AssociatedField = {
	type: ENVIRONMENT_FIELD_TYPE,
	custom: false,
	global: false,
	key: 'environment',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0016',
	description: '',
	name: 'Environment',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const versionField: AssociatedField = {
	type: VERSION_CF_TYPE,
	custom: true,
	global: false,
	key: 'vegemiteVersionField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0027',
	description: 'valid version description',
	name: 'Vegemite on toast',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const multiVersionField: AssociatedField = {
	type: MULTI_VERSION_CF_TYPE,
	custom: true,
	global: false,
	key: 'meatPieMultipleVersionField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0028',
	description: 'valid multi version description',
	name: 'Meat pies',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const multiCheckboxField: AssociatedField = {
	type: MULTI_CHECKBOXES_CF_TYPE,
	options: validOptions,
	custom: true,
	global: false,
	key: 'MULTI_CHECKBOXES_CF_TYPE',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0013',
	description: 'MULTI_CHECKBOXES_CF_TYPE description',
	name: 'Ice cream flavor',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const dropdownField: AssociatedField = {
	type: SELECT_CF_TYPE,
	options: validOptions,
	custom: true,
	global: false,
	key: 'dropdown-field',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0012',
	description: 'validField description',
	name: 'Ice cream flavor',
	category: fieldCategoryId,
	required: false,
	operations: defaultFieldOperations,
	provider: defaultFieldProvider,
	availability: null,
};

export const groupPickerField: AssociatedField = {
	type: GROUP_CF_TYPE,
	custom: true,
	global: false,
	key: 'group-picker-field',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0017',
	description: 'valid group picker description',
	name: 'Fairy bread',
	category: fieldCategoryId,
	required: false,
	operations: {
		editable: true,
		canModifyRequired: true,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: true,
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const multiGroupPickerField: AssociatedField = {
	type: MULTI_GROUP_CF_TYPE,
	custom: true,
	global: false,
	key: 'validField',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0018',
	description: 'valid multi group picker description',
	name: 'Lamingtons',
	category: fieldCategoryId,
	required: false,
	operations: {
		editable: true,
		canModifyRequired: true,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: true,
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const reporter: Field = {
	key: 'reporter',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0001',
	name: 'Reporter',
	type: 'reporter',
	custom: false,
	global: false,
	description: '',
	operations: {
		editable: false,
		canModifyRequired: true,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: false,
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const priorityField: Field = {
	key: 'priority',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0002',
	name: 'Priority',
	type: 'priority',
	custom: false,
	global: false,
	description: '',
	operations: {
		editable: false,
		canModifyRequired: true,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: false,
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const associatedPriorityField: AssociatedField = {
	...priorityField,
	category: 'category-1',
	required: false,
};

export const duedate: Field = {
	key: 'duedate',
	externalUuid: 'deadbeef-dead-4ead-beef-deadbeef0004',
	name: 'Due date',
	type: 'duedate',
	custom: false,
	global: false,
	description: '',
	operations: {
		editable: true,
		canModifyRequired: false,
		canModifyOptions: true,
		canModifyDefaultValue: true,
		canModifyPropertyConfiguration: true,
		removable: true,
		categoriesWhitelist: [],
		canAssociateInSettings: true,
		deletable: false,
	},
	provider: defaultFieldProvider,
	availability: null,
};

export const exampleFieldConfigurationComponentProps: FieldConfigurationComponentProps = {
	field: versionField,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onUpdate: () => {},
	layoutOwners: [],
	projectId: '',
	projectKey: '',
	fieldErrors: [],
	limits: {
		optionsPerField: 10,
	},
	extraDefinerId: null,
};
